import React, { Fragment, useEffect, useState } from "react";
import PageHeader from "../../common/components/PageHeader";
import { Box, Divider, Grid, IconButton, Stack, Typography } from "@mui/material";
import CommonButton from "../../common/components/CommonButton";
import { Add, Close, Description } from "@mui/icons-material";
import theme from "../../theme";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import CommonSelect from "../../common/components/CommonSelect";
import Dropzone from "react-dropzone";
import TicketListing from "./TicketListing";
import FAQAccordion from "../FAQ/FAQAccordion";
import CurrentPlan from "../../common/components/CurrentPlan";
import { ExamPlans, TicketCategory, TicketStatus, UserTypeId } from "../../common/const";
import CommonCard from "../../common/components/CommonCard";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { createTicket, getAllTickets } from "../../api/supportTicketApi";
import SimplePagination from "../LandingPage/SimplePagination";

const TicketType = {
    All: 'All Tickets',
    Open: 'Open',
    Closed: 'Closed'
}


const Support = () => {
    const location = useLocation();
    const { loading, user } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const [serviceType, setServiceType] = useState(TicketType.All);
    const [addOpen, setAddOpen] = useState(false);
    const [fileNames, setFileNames] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        description: '',
        priority: 'HIGH',
    });
    const [pageNo, setPageNo] = useState(1);
    const pageSize = 8;
    const [errors, setErrors] = useState({});
    const [ticketData, setTicketData] = useState([]);

    const handleDrop = acceptedFiles => {
        setFileNames(acceptedFiles?.map(files => files));
    };

    const getTicketData = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getAllTickets(pageNo, pageSize, TicketStatus[serviceType]);
            setTicketData(response.data);
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message ||
                err.response?.data?.message ||
                err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    }

    useEffect(() => {
        getTicketData();
    }, [pageNo, serviceType]);

    const handlePageChange = (page) => {
        setPageNo(page);
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'title':
                if (!value) return 'Subject is required';
                return '';
            case 'category':
                if (!value) return 'Category is required';
                return '';
            case 'description':
                if (!value) return 'Description is required';
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const data = new FormData();
            data.append('subject', formData.title);
            data.append('description', formData.description);
            data.append('category', formData.category);
            data.append('priority', formData.priority);
            if (fileNames.length > 0) {
                fileNames.map((a) => {
                    data.append('attachments', a);
                });
            }
            const response = await createTicket(data);
            if (response.success) {
                showNotification(response.message, 'success');
                setFormData({
                    title: '',
                    category: '',
                    description: '',
                    priority: 'HIGH',
                });
                setAddOpen(false);
                dispatch(setLoading({
                    loading: false
                }));
                getTicketData();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={addOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Create New Ticket</Typography>
                                <Typography fontSize={13} color={theme.palette.background.dark}>Enter the ticket details</Typography>
                            </Box>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setAddOpen(false);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container mt={0.2} mb={2} spacing={1.6}>
                        <Grid item xs={12} md={12}>
                            <CommonFormField
                                label="Subject"
                                type="email"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                error={errors.title}
                                placeholder='What is the issue about'
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <CommonSelect
                                label="Category"
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                placeholder="Select Category"
                                error={errors.category}
                                options={TicketCategory}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <CommonFormField
                                label="Description"
                                type="email"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder='Add description related to your query'
                                multiline
                                minRows={3}
                            />
                        </Grid>
                        <Grid item md={12}>
                            <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: 'text.main', mb: 0.8 }}>
                                Set Priority
                            </Typography>
                            <Box
                                display="flex"
                                gap={1}
                            >
                                <CommonButton
                                    label='High'
                                    size="small"
                                    height="35px"
                                    marginBottom="0px"
                                    fontWeight="bold"
                                    background={formData.priority === 'HIGH' ? '#FFE2E2' : theme.palette.background.main}
                                    color={formData.priority === 'HIGH' ? 'red' : theme.palette.background.dark}
                                    handleClick={() => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            priority: 'HIGH'
                                        }))
                                    }}
                                    border={formData.priority !== 'HIGH' ? "1px solid lightgray" : '1px solid #FF1010'}
                                />
                                <CommonButton
                                    label='Medium'
                                    size="small"
                                    height="35px"
                                    marginBottom="0px"
                                    fontWeight="bold"
                                    background={formData.priority === 'MEDIUM' ? '#FEF9C2' : theme.palette.background.main}
                                    color={formData.priority === 'MEDIUM' ? '#894B00' : theme.palette.background.dark}
                                    handleClick={() => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            priority: 'MEDIUM'
                                        }))
                                    }}
                                    border={formData.priority !== 'MEDIUM' ? "1px solid lightgray" : '1px solid #894B00'}
                                />
                                <CommonButton
                                    label="Low"
                                    height="35px"
                                    marginBottom="0px"
                                    fontWeight="bold"
                                    background={formData.priority === 'LOW' ? theme.palette.success.light : theme.palette.background.main}
                                    color={formData.priority === 'LOW' ? '#01875F' : theme.palette.background.dark}
                                    handleClick={() => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            priority: 'LOW'
                                        }))
                                    }}
                                    border={formData.priority !== 'LOW' ? "1px solid lightgray" : `1px solid #01875F`}
                                />
                            </Box>
                        </Grid>
                        <Grid item md={12}>
                            <Box sx={{ width: '100%' }}>
                                {fileNames.length > 0 ? (
                                    // Show selected files
                                    <Box sx={{ mt: 2 }}>
                                        {fileNames.map((file, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    backgroundColor: 'background.paper'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Description color="primary" />
                                                    <Box>
                                                        <Typography variant="body1">{file.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(file.size / 1024).toFixed(2)} KB
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setFileNames(fileNames.splice(index, -1))}
                                                    color="error"
                                                >
                                                    <Close />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    // Show dropzone when no files
                                    <Dropzone
                                        onDrop={handleDrop}
                                    >
                                        {({ getRootProps, getInputProps, isDragActive }) => (
                                            <Box
                                                {...getRootProps()}
                                                sx={{
                                                    border: '2px dashed',
                                                    borderColor: '#D8DAEB',
                                                    borderRadius: 2,
                                                    padding: { xs: 3, md: 6 },
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    height: '25vh',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <input {...getInputProps()} />
                                                <img src="/Images/sound.png" alt="upload" />
                                                <Typography fontWeight={600} gutterBottom>
                                                    Drag and drop your files
                                                </Typography>
                                                <CommonButton
                                                    label="Select File"
                                                    color="Black"
                                                    background="White"
                                                    border={`1px solid ${theme.palette.background.secondary}`}
                                                    width="100px"
                                                    height="40px"
                                                    marginBottom="0px"
                                                    marginTop="10px"
                                                />
                                            </Box>
                                        )}
                                    </Dropzone>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label="Create Ticket"
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={() => {
                            handleSubmit();
                        }}
                    />
                )}
            />
            <PageHeader
                title="DCP Help Desk & Support"
                content={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                            Need help? We're here to solve your issue quickly and easily.
                        </Typography>
                    </Stack>
                }
            />
            <Grid container spacing={2}>
                <Grid item sm={location.pathname === '/support-help-desk' ? 12 : 9}>
                    {location.pathname !== '/support-help-desk' && (
                        <FAQAccordion />
                    )}
                    {location.pathname !== '/support-help-desk' && (
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={3}>
                            <Box display="flex" gap={1}>
                                <CommonButton
                                    label={TicketType.All}
                                    width="120px"
                                    fontWeight={600}
                                    border={serviceType !== TicketType.All ? `1px solid #0000001A` : 'none'}
                                    color={serviceType === TicketType.All ? theme.palette.secondary.main : 'gray'}
                                    background={serviceType === TicketType.All ? theme.palette.primary.main : '#FAFAFA'}
                                    handleClick={() => setServiceType(TicketType.All)}
                                    marginBottom="0px"
                                />
                                <CommonButton
                                    label={TicketType.Open}
                                    width="80px"
                                    fontWeight={600}
                                    border={serviceType !== TicketType.Open ? `1px solid #0000001A` : 'none'}
                                    color={serviceType === TicketType.Open ? theme.palette.secondary.main : 'gray'}
                                    background={serviceType === TicketType.Open ? theme.palette.primary.main : '#FAFAFA'}
                                    handleClick={() => setServiceType(TicketType.Open)}
                                    marginBottom="0px"
                                />
                                <CommonButton
                                    label={TicketType.Closed}
                                    width="80px"
                                    fontWeight={600}
                                    border={serviceType !== TicketType.Closed ? `1px solid #0000001A` : 'none'}
                                    color={serviceType === TicketType.Closed ? theme.palette.secondary.main : 'gray'}
                                    background={serviceType === TicketType.Closed ? theme.palette.primary.main : '#FAFAFA'}
                                    handleClick={() => setServiceType(TicketType.Closed)}
                                    marginBottom="0px"
                                />
                            </Box>
                            {(location.pathname !== '/support-help-desk' && user?.user_type_id === UserTypeId.Student) && (
                                <CommonButton
                                    marginBottom="0px"
                                    label="Create New Ticket"
                                    startIcon={<Add />}
                                    width="200px"
                                    fontWeight={600}
                                    handleClick={() => setAddOpen(true)}
                                />
                            )}
                        </Box>
                    )}
                    <TicketListing pathname={location.pathname} ticketData={ticketData?.data} />
                    {ticketData?.data?.length > 0 && (
                        <SimplePagination
                            currentPage={pageNo}
                            totalPages={ticketData?.pagination?.totalPages}
                            onPageChange={handlePageChange}
                        />
                    )}
                </Grid>
                {location.pathname !== '/support-help-desk' && (
                    <Grid item sm={3} mt={1.5}>
                        <CurrentPlan />
                        {ExamPlans.slice(1, 3).map((a, index) => (
                            <CommonCard
                                key={index}
                                paddingBottom="0px"
                                borderRadius={1}
                                padding="10px"
                                content={
                                    <Box
                                        sx={{
                                            background: '#E0E0E080',
                                            borderRadius: 1,
                                            padding: 2
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <img
                                                src={`/Images/${a.name}.png`}
                                                alt="subscription-logo"
                                                height="60px"
                                            />
                                            <Box sx={{ width: '100%' }}>
                                                <Box display="flex" gap={1} alignItems="center" justifyContent="space-between">
                                                    <Typography variant="h5">{a.name}</Typography>
                                                    {a.isActive && (
                                                        <Box
                                                            sx={{
                                                                background: theme.palette.background.main,
                                                                textAlign: 'center',
                                                                borderRadius: 0.8,
                                                                width: 60,
                                                                height: 25,
                                                                marginRight: 1
                                                            }}
                                                        >
                                                            <Typography sx={{ color: theme.palette.success.main }}>Active</Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Typography sx={{ fontWeight: 600 }}>${a.price} <span style={{ color: theme.palette.icon.light, fontWeight: 'normal' }}>/ month</span></Typography>
                                            </Box>
                                        </Box>
                                        <Typography mt={1} color={theme.palette.background.dark}>You're on a solid path with notable strengths in several areas. Your profile shows promise.</Typography>
                                    </Box>
                                }
                                footerContent={
                                    <CommonButton
                                        label="Upgrade"
                                        size="md"
                                        marginBottom="0px"
                                        height='35px'
                                        marginTop="0px"
                                    />
                                }
                            />
                        ))}
                    </Grid>
                )}
            </Grid>
        </Fragment>
    )
}

export default Support;